import { useEffect, useMemo } from 'react';
import { boundingExtent } from 'ol/extent';

import { useProject } from '@contexts/Project.context';
import { useCollectionStore } from '@stores/useCollectionStore';
import { useVectorLayer } from '@hooks/useVectorLayer';
import { imagePositionsToMapCoordinates } from '@utils/map/helpers';
import { COLLECTION_LAYER_ID } from '@utils/constants';

import { filterFeatures } from './utils/transformUtils';
import { buildShapeFeature, resolveShape } from './utils/collectionMapSource';
import { buildShapeStyleFunction } from './utils/collectionMapStyle';
import { DETECTION_POINTER } from './constants';

/**
 * Records which shape each built feature ended up as, so the layer's style
 * function can dispatch per feature. Needed because a fallback can leave the
 * per-image overlay holding a mix of outlines, boxes and points.
 */
const SHAPE_PROPERTY = 'collection_shape';

const LAYER_ID = COLLECTION_LAYER_ID.SINGLE_IMAGE;

/** Sits just above the model's own per-image layers (image 21, annotations 23,
 *  detections 24) — the map-level collection shapes (102-104) are hidden while
 *  an image is open, so no collision there. */
const Z_INDEX = 25;

/**
 * Converts one normalized [0,1] image-space point to map coordinates within an
 * extent — the single-point counterpart to imagePositionsToMapCoordinates
 * (which only operates on polygon rings). Same math, kept as a dedicated
 * helper rather than wrapping a point as a one-vertex "ring" for clarity.
 */
const imagePositionToMapCoordinate = ([px, py], extent) => {
	const xLength = extent[2] - extent[0];
	const yLength = extent[3] - extent[1];
	return [xLength * px + extent[0], yLength * (1 - py) + extent[1]];
};

/**
 * Normalized → map-space for a GeoJSON geometry.
 *
 * `imagePositionsToMapCoordinates` walks exactly two levels (rings → positions),
 * which is a Polygon. A MultiPolygon has one more, and feeding it through
 * unchanged yields `[NaN, NaN]` throughout — coordinates that build a geometry
 * without throwing and then render nothing. Segmentation masks are the case that
 * arrives as MultiPolygon, so the type has to drive the walk.
 */
const transformGeometry = (geometry, extent) => {
	// `geometrySource` accepts a bare coordinate ring as well as a geometry
	// object, so one has to be transformed too — left in normalized space it
	// still builds, and renders as a speck at the map origin.
	if (Array.isArray(geometry)) return imagePositionsToMapCoordinates(geometry, extent);
	if (!geometry?.coordinates) return geometry;
	const coordinates =
		geometry.type === 'MultiPolygon'
			? geometry.coordinates.map(polygon => imagePositionsToMapCoordinates(polygon, extent))
			: imagePositionsToMapCoordinates(geometry.coordinates, extent);
	return { ...geometry, coordinates };
};

const transformCenter = (center, extent) => {
	if (center?.x == null || center?.y == null) return center;
	const [x, y] = imagePositionToMapCoordinate([center.x, center.y], extent);
	return { x, y };
};

/**
 * Transforms one normalized collection detection feature into the same shape
 * buildShapeFeature expects from a 3857 feature — geometry coordinates, center,
 * and bbox all converted into real map coordinates within the open image's
 * extent — so the existing, unmodified buildShapeFeature builds the OL
 * geometry the same way regardless of which projection the feature came in.
 */
const transformToMapSpace = (feature, extent) => ({
	...feature,
	geometry: transformGeometry(feature.geometry, extent),
	properties: {
		...feature.properties,
		center: transformCenter(feature.properties?.center, extent),
		bbox: feature.properties?.bbox?.length
			? imagePositionsToMapCoordinates(feature.properties.bbox, extent)
			: feature.properties?.bbox,
		mask_geometry: transformGeometry(feature.properties?.mask_geometry, extent),
	},
});

/**
 * Renders collection detections precisely on top of the currently-opened
 * single image — the per-image counterpart to CollectionMapLayer's map-level
 * rendering. Mounted only for single-image projects.
 *
 * Uses a plain OL VectorLayer (not WebGL): the per-image feature count is
 * small and only changes on image-open/filter-apply/shape-switch, so a full
 * rebuild on each change is simpler and cheap enough — no benefit to WebGL's
 * shader-based rendering or CollectionMapLayer's always-mounted 3-layer/diffing
 * setup at this scale.
 *
 * @param {object} props
 * @param {Array}  props.normalizedFeatures - all collection features in normalized image-space (from useCollectionNormalizedFeatures)
 * @param {{ collectionFilterValues, objectFilterValues }} props.filterValues - from useCollectionFilterValues
 * @param {Array}  props.objects - collection objects (key, color, opacity)
 * @param {{ objectKey, color?, opacity? }|null} props.liveStyling - live style override for the object being edited
 */
const CollectionSingleImageLayer = ({ normalizedFeatures, filterValues, objects, liveStyling }) => {
	const { mapObject, selectedSingleImage } = useProject();
	const checkedNodes = useCollectionStore(s => s.checkedNodes);

	const layer = useVectorLayer(mapObject, LAYER_ID, {
		name: 'Collection detections (image)',
		zIndex: Z_INDEX,
		visible: false,
	});

	// Visible features for whichever image is open. Filtered on demand, scoped
	// to that one tile's small subset — not a project-wide computation, since
	// this is the only consumer and it only ever needs one tile at a time.
	const imageFeatures = useMemo(() => {
		if (!selectedSingleImage) return [];
		const tileName = selectedSingleImage.get('name');
		const tileFeatures = normalizedFeatures.filter(
			feature => feature.properties?.tile_name === tileName
		);
		return filterFeatures(
			tileFeatures,
			checkedNodes,
			filterValues.collectionFilterValues,
			filterValues.objectFilterValues
		);
	}, [selectedSingleImage, normalizedFeatures, checkedNodes, filterValues]);

	const styleFunctions = useMemo(
		() => buildShapeStyleFunction(objects, liveStyling),
		[objects, liveStyling]
	);

	useEffect(() => {
		layer?.setVisible(!!selectedSingleImage);
	}, [layer, selectedSingleImage]);

	// Rebuild whenever the open image or its visible features change. Applies the
	// style in the same pass as the feature update so the layer always renders
	// with the correct style — avoids the default-style flash that can happen if
	// setStyle and addFeatures are in separate effects.
	useEffect(() => {
		if (!layer || !selectedSingleImage) return;

		const extent = boundingExtent(selectedSingleImage.get('polygons'));

		// Always asks for an outline regardless of the object's map-level pointer —
		// at per-image zoom the detection polygon is what matters, not a dot.
		// `resolveShape` falls a detection with no outline back to its box or
		// center point rather than dropping it from the view.
		const olFeatures = imageFeatures
			.map(feature => {
				const mapSpace = transformToMapSpace(feature, extent);
				const shape = resolveShape(mapSpace, DETECTION_POINTER.OUTLINE);
				if (!shape) return null;
				const olFeature = buildShapeFeature(mapSpace, shape);
				// `resolveShape` only clears the structural checks; the OL
				// constructor can still reject the coordinates, and one malformed
				// detection must not take the whole image view down.
				if (!olFeature) return null;
				olFeature.set(SHAPE_PROPERTY, shape);
				return olFeature;
			})
			.filter(Boolean);

		// Shapes are mixed, so style is dispatched per feature.
		layer.setStyle(feature => styleFunctions[feature.get(SHAPE_PROPERTY)](feature));

		const source = layer.getSource();
		source.clear();
		if (olFeatures.length) source.addFeatures(olFeatures);
	}, [layer, selectedSingleImage, imageFeatures, styleFunctions]);

	return null;
};

export default CollectionSingleImageLayer;
